import { Input, Select } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useGetEventCategory, useGetMappedEvents } from '@src/api/emp/queryHooks';
import { MappedEventTypes } from '@src/common/types/empTypes';
import { validateMappedEventName } from '@src/emp-3.0/domain/mapped-event';

import styles from './index.less';

interface IEventNameBuilder {
  mappedEvent: MappedEventTypes | undefined;
  eventCategory: string | undefined;
  mappedName: string | undefined;
  onChange: (value: { [key: string]: any }) => void;
  onEventCategoryChange: (eventCategory: string) => void;
  onMappedNameChange: (mappedName: string) => void;
  onPredefinedMappingRulesChange: (
    predefinedMappedEvent: MappedEventTypes | undefined,
    status?: 'edited' | 'not edited',
  ) => void;
  predefineUnsavedChanges: Map<string, MappedEventTypes>;
}

const EventNameBuilder: React.FC<IEventNameBuilder> = (props: IEventNameBuilder) => {
  const {
    mappedEvent,
    eventCategory,
    mappedName,
    onChange,
    onEventCategoryChange,
    onMappedNameChange,
    onPredefinedMappingRulesChange,
    predefineUnsavedChanges,
  } = props;
  const [illegal, setIllegal] = useState<boolean>(false);
  const [repeated, setRepeated] = useState<boolean>(false);
  const [mismatch, setMismatch] = useState<boolean>(false);
  const [greyCategory, setGreyCategory] = useState<boolean>(false);
  const lastClickItem = useRef<'category' | 'name'>();
  const predefinedNames = useRef<string[]>([]);
  const { data: categoryData } = useGetEventCategory();
  const { data: allMappedEvents } = useGetMappedEvents();
  const isUpdateMode = !!mappedEvent?.event_id;

  useEffect(() => {
    onChange({ mismatch, illegal, repeated });
  }, [mismatch, illegal, repeated]);

  useEffect(() => {
    predefinedNames.current = [];
    categoryData?.data?.forEach((category) => {
      predefinedNames.current.push(...(category?.predefined_event_names ?? []));
    });
  }, [categoryData]);

  useEffect(() => {
    if (
      mappedName &&
      eventCategory &&
      !categoryData?.data
        ?.find((category) => category?.category_name === eventCategory)
        ?.predefined_event_names?.includes(mappedName) &&
      predefinedNames.current.includes(mappedName)
    ) {
      setMismatch(true);
      if (lastClickItem.current === 'category') {
        setGreyCategory(false);
      } else if (lastClickItem.current === 'name') {
        setGreyCategory(true);
      }
    } else {
      setMismatch(false);
      setGreyCategory(false);
    }
  }, [mappedName, eventCategory, categoryData]);

  const categoryItems = useMemo(() => {
    return categoryData?.data?.map((v) => {
      return { label: v.category_name, value: v.category_name };
    });
  }, [categoryData]);

  const handleEventCategoryChange = (eventCategory: string) => {
    lastClickItem.current = 'category';
    onEventCategoryChange?.(eventCategory);
  };

  const handleMappedNameChange = (e) => {
    const name = e.target.value;
    lastClickItem.current = 'name';
    onMappedNameChange?.(name);
    // TODO merge all validate in one function share with other component.
    if (validateMappedEventName(name).failed) {
      setIllegal(true);
      return;
    }
    setIllegal(false);

    if (
      isUpdateMode &&
      predefinedNames.current.includes(name) &&
      allMappedEvents?.data?.find((mappedEvent) => mappedEvent.event_name === name)
    ) {
      setRepeated(name !== mappedEvent.event_name);
      return;
    }
    setRepeated(false);

    // handle pre-defined rules
    if (predefinedNames.current.includes(name)) {
      let pdfMappedEvent;
      let status: 'edited' | 'not edited' = 'not edited';
      if (predefineUnsavedChanges.get(name)) {
        pdfMappedEvent = predefineUnsavedChanges.get(name);
        status = 'edited';
      } else {
        pdfMappedEvent = allMappedEvents?.data?.find(
          (mappedEvent) => mappedEvent.event_name === name,
        );
      }
      if (pdfMappedEvent) {
        onPredefinedMappingRulesChange?.(pdfMappedEvent, status);
        return;
      }
    }
    onPredefinedMappingRulesChange?.(undefined);
  };

  let notificationText = '';
  if (illegal) {
    notificationText = validateMappedEventName(mappedName || '').message;
  } else if (mismatch) {
    notificationText = 'Pre-defined name and category mismatched';
  }

  return (
    <div className={styles.rootContainer}>
      <div className={styles.title}>Set Mapped Event</div>
      <div className={styles.selectContainer}>
        <div className={styles.itemKey}>Select Event Category *</div>
        <div className={styles.itemVal}>
          <Select
            value={eventCategory}
            style={{
              width: '100%',
              fontWeight: 'normal',
              fontSize: '14px',
              color: greyCategory ? '#949494' : '#494949',
            }}
            size="small"
            options={categoryItems}
            placeholder="Please Select..."
            onChange={handleEventCategoryChange}
            popupClassName={styles.eventCategorySelect}
          />
        </div>
        <div className={styles.itemKey}>Mapped Event Name *</div>
        <div className={styles.itemVal}>
          <Input
            className={styles.mappedEventNameInput}
            placeholder="Please input..."
            value={mappedName}
            onChange={handleMappedNameChange}
            style={{ width: '100%' }}
          />
        </div>
        {notificationText && (
          <div className={styles.mismatchNotification}>
            <div className={styles.mismatchText}>{notificationText}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventNameBuilder;
